import * as React from "react";
import { Check, ChevronsUpDown, PenLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export const MEXICAN_STATES = [
  'Aguascalientes','Baja California','Baja California Sur','Campeche',
  'Chiapas','Chihuahua','Ciudad de Mexico','Coahuila','Colima',
  'Durango','Estado de Mexico','Guanajuato','Guerrero','Hidalgo',
  'Jalisco','Michoacan','Morelos','Nayarit','Nuevo Leon','Oaxaca',
  'Puebla','Queretaro','Quintana Roo','San Luis Potosi','Sinaloa',
  'Sonora','Tabasco','Tamaulipas','Tlaxcala','Veracruz','Yucatan','Zacatecas'
];

export const COUNTRIES = [
  'Mexico','Estados Unidos','Canada','Guatemala','Belice','Honduras',
  'El Salvador','Nicaragua','Costa Rica','Panama','Colombia','Venezuela',
  'Ecuador','Peru','Brasil','Bolivia','Chile','Argentina','Uruguay','Paraguay',
  'Cuba','Republica Dominicana','Puerto Rico','Haiti','Jamaica','Trinidad y Tobago',
  'Espana','Francia','Alemania','Italia','Reino Unido','Portugal','Paises Bajos',
  'Belgica','Suiza','Austria','Suecia','Noruega','Dinamarca','Finlandia','Irlanda',
  'Grecia','Polonia','Republica Checa','Rumania','Hungria','Rusia','Ucrania','Turquia',
  'Japon','China','Corea del Sur','India','Indonesia','Tailandia','Vietnam','Filipinas',
  'Malasia','Singapur','Australia','Nueva Zelanda','Egipto','Marruecos','Sudafrica',
  'Nigeria','Kenia','Arabia Saudita','Emiratos Arabes Unidos','Israel','Otro'
];

const MANUAL_VALUE = '__manual__';

export const SearchableSelect = ({
  value,
  onValueChange,
  options,
  placeholder = "Seleccionar...",
  showManualOption = false,
  manualLabel = "Agregar manualmente",
  className = "",
  triggerClassName = "",
  size = "default",
  "data-testid": testId,
}) => {
  const [open, setOpen] = React.useState(false);
  const [isManual, setIsManual] = React.useState(false);
  const [manualValue, setManualValue] = React.useState('');

  // Detect if value is not in options (was manually entered)
  React.useEffect(() => {
    if (value && !options.includes(value) && value !== '') {
      setIsManual(true);
      setManualValue(value);
    }
  }, []);

  const handleSelect = (selected) => {
    if (selected === MANUAL_VALUE) {
      setIsManual(true);
      setManualValue('');
      onValueChange('');
      setOpen(false);
      return;
    }
    setIsManual(false);
    setManualValue('');
    onValueChange(selected);
    setOpen(false);
  };

  const handleManualChange = (e) => {
    const v = e.target.value;
    setManualValue(v);
    onValueChange(v);
  };

  const handleBackToDropdown = () => {
    setIsManual(false);
    setManualValue('');
    onValueChange('');
  };

  const sizeClasses = size === "sm" ? "h-8 text-xs" : "h-9 text-sm";

  if (isManual) {
    return (
      <div className={cn("flex gap-1", className)}>
        <Input
          value={manualValue}
          onChange={handleManualChange}
          placeholder={placeholder}
          className={cn(sizeClasses, "flex-1")}
          data-testid={testId ? `${testId}-manual` : undefined}
          autoFocus
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("flex-shrink-0", size === "sm" ? "h-8 w-8" : "h-9 w-9")}
          onClick={handleBackToDropdown}
          title="Volver al dropdown"
        >
          <ChevronsUpDown className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", sizeClasses, triggerClassName)}
          data-testid={testId}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Buscar ${placeholder.toLowerCase()}...`} className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {showManualOption && (
                <CommandItem value={MANUAL_VALUE} onSelect={() => handleSelect(MANUAL_VALUE)} className="text-blue-600 font-medium">
                  <PenLine className="mr-2 h-3 w-3" />
                  {manualLabel}
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem key={opt} value={opt} onSelect={() => handleSelect(opt)}>
                  <Check className={cn("mr-2 h-3 w-3", value === opt ? "opacity-100" : "opacity-0")} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
